//@ts-check
import { LmvMatrix4 as Matrix4 } from '../../wgs/scene/LmvMatrix4';
import { LmvBox3 as Box3, LmvBox3 } from '../../wgs/scene/LmvBox3';
import { Model } from "../../application/Model";
import { MeshFlags } from '../../wgs/scene/MeshFlags';
import THREE from "three";
import * as et from "../../application/EventTypes";
import { RcsExtractorCore } from './RcsExtractorCore.js';
import { RcsParser } from './RcsParser.js';
import { setRcsFileLoaderReference } from './RcsIO.js';
import { isLocalhostUrl } from './RcsUrlUtils.js';
import {
  USE_OLD_RANGER, INSTANCE_TREE_MODE, RCS_NUM_IN_FLIGHT_REQUESTS_HTTP, MAX_LOD_LEVELS,
  RCS_FLOATS_PER_POINT } from
'./RcsConstants.js';
import { getRequiredLodLevels, computeDirectModeLevelsInLod0, computeMaxCachedLodLevel } from './RcsLodMapping.js';
import { ProgressState } from '../../application/ProgressState';
import { FileLoaderManager } from "../../application/FileLoaderManager";
import { BufferGeometryUtils } from '../../wgs/scene/BufferGeometry';
import { getRcsStageConfiguration, StageNames } from '../../wgs/scene/out-of-core-tile-manager/tasks/defaultStageConfiguration';
import { USE_OUT_OF_CORE_TILE_MANAGER, USE_WEBGPU, RCS_POINTCLOUD_MATERIAL_ID, INVALID_GEOM_ID } from '../../wgs/globals';
import { fromUrlSafeBase64 } from '../../globals';
import { initLoadContext, getDeveloperApiUrl } from "../net/endpoints";
import { token as viewerToken } from "../../envinit";
import { ROOT_LOADED_PROGRESS } from "../main/constants.js";
import { ViewingService } from "../net/Xhr";
import { RcsSimplePropDbLoader } from './RcsSimplePropDbLoader.js';
import { RcsDebugOctreePropDbLoader } from './RcsDebugOctreePropDbLoader.js';
import { RcsPackage } from './RcsPackage.js';
import { POINT_CLOUD_CACHE_LIMIT, POINT_CLOUD_CACHE_PRESETS } from '../../application/PreferenceNames.js';
import { FRAGMENTS_UNLOADED_EVENT } from "../../application/EventTypes";
import { rcsWorkerPool } from "./RcsWorkerPool";
import { RcsOpfsCacheClient } from './RcsOpfsCacheClient.js';
import { RCS_OPFS_CACHE_DIR } from './RcsOpfsCache.js';
import { RcsOpfsCacheStats } from './RcsOpfsCacheStats.js';

/** @type {boolean} One-time guard for the localhost dev-load warning in {@link getStageConfiguration}. */
let localhostLoadWarningLogged = false;

/** @import { Viewer3DImpl } from "../../application/Viewer3DImpl"; */
/** @import { MeshData } from '../../wgs/scene/BufferGeometry' */
/** @import { OutOfCoreTileManagerStageConfiguration } from '../../wgs/scene/out-of-core-tile-manager/OutOfCoreTileManager' */
/** @typedef {import('../../wgs/scene/BufferGeometry').LeanBufferGeometry} LeanBufferGeometry */


// ── RCS Type Definitions ─────────────────────────────────────────────

/**
 * Members shared by every octree node, regardless of whether it is a leaf.
 * @typedef {Object} OctreeNodeBase
 * @property {number}    id - Node ID
 * @property {number[]}  boundingBox - Bounding box [minX, minY, minZ, maxX, maxY, maxZ]
 * @property {number[]}  children - Child node IDs (empty for leaves)
 * @property {{ svoDataSize?: number, leafIndex?: number, svoDataOffset?: number }} [fileOffsets] - Developer-tool scratch data (populated by the RcsBvhExtractor JSON exporter).
 * @property {number}    [totalLeafVolume] - Total volume of all leaf nodes below this node (computed during build)
 */

/**
 * Members that are present only on umbrella leaf nodes (`isLeaf === true`).
 * @typedef {Object} OctreeLeafData
 * @property {true}      isLeaf - Discriminant: always `true` for leaf nodes
 * @property {number}    fragmentId - Fragment ID (direct mapping to the umbrella leaf index)
 * @property {number[]}  numSvoLeafNodes - SVO leaf point counts per level
 * @property {number[]}  numNonLeafNodes - SVO interior-node counts per level
 * @property {number[]}  nodeBounds - Tight AABB reported by the source leaf
 */

/**
 * Umbrella leaf node: owns SVO point data and has no children.
 * @typedef {OctreeNodeBase & OctreeLeafData} OctreeLeafNode
 */

/**
 * Members that are present only on interior octree nodes (`isLeaf === false`).
 * @typedef {Object} OctreeInteriorData
 * @property {false} isLeaf - Discriminant: always `false` for interior nodes
 */

/**
 * Interior octree node: has children and no SVO leaf data.
 * @typedef {OctreeNodeBase & OctreeInteriorData} OctreeInteriorNode
 */

/**
 * Octree node from the source umbrella octree structure.
 *
 * Discriminated union on the `isLeaf` flag: the leaf-only members
 * (`fragmentId`, `numSvoLeafNodes`, `numNonLeafNodes`, `nodeBounds`) are present
 * if and only if `isLeaf === true`. Narrow with `if (node.isLeaf)` to access them.
 * @typedef {OctreeLeafNode | OctreeInteriorNode} OctreeNode
 */

/**
 * Complete octree structure with annotated volumes.
 * @typedef {Object} RcsOctreeStructure
 * @property {OctreeNode[]} nodes - Array of octree nodes with totalLeafVolume annotations
 */

/**
 * Return value of {@link RcsFileLoader.getStats}.
 * @typedef {Object} RcsLoadStats
 * @property {Object<number, number>} lodNodeCounts - Loaded nodes per LOD level (only updated when {@link RcsFileLoader.setLoadStatsTrackingEnabled} is on)
 * @property {number} inFlightRequests - Number of outstanding RCS worker fetch/decode requests
 * @property {number} inFlightRequestsSize - Total byte size of in-flight worker requests
 * @property {number} totalReceivedBytes - Cumulative bytes read from RCS files (I/O + worker)
 * @property {number} nodesLoadedPerSecond - Leaf LOD fragments activated in the last second (excludes inner HLOD nodes)
 * @property {number} totalNodesLoaded - Cumulative leaf LOD fragment activations since stats tracking was enabled
 * @property {number} innerNodesLoaded - Resident inner HLOD fragments (LOD level −1)
 * @property {number} totalFragmentsLoaded - All activated fragments, including inner HLOD nodes
 * @property {number} [cacheHits] - OPFS geometry cache hits (inner nodes, leaf LODs, and LOD-0; includes {@link lod0CacheHits})
 * @property {number} [cacheMisses] - Number of OPFS cache misses since process start
 * @property {number} [cacheBytesLoaded] - Bytes loaded from OPFS cache
 * @property {number} [networkBytesLoaded] - Bytes loaded from network (cache misses)
 * @property {number} [lod0CacheHits] - LOD-0 decoded-blob cache hits (subset of {@link cacheHits})
 * @property {number} [lod0CacheMisses] - LOD-0 decoded-blob cache misses (see field docs)
 * @property {boolean} [opfsCacheEnabled] - Whether OPFS caching is enabled
 */

/**
 * Decoded point data returned by the RCS worker.
 * @typedef {Object} RcsDecodedPoints
 * @property {number}       pointCount - Number of points
 * @property {Float32Array} interleavedData - Interleaved XYZRGB (6 floats per point)
 */

/**
 * Per-level decoded points returned by the RCS_DECODE_BY_LEVEL worker.
 * @typedef {Object} RcsLevelData
 * @property {Float32Array} interleavedData - Interleaved XYZRGB for this level
 * @property {number}       count - Number of points at this octree level
 * @property {number}       level - Octree level index
 */

/**
 * Result of RCS_DECODE_BY_LEVEL worker operation.
 * @typedef {Object} RcsLevelDecodeResult
 * @property {RcsLevelData[]} pointsByLevel - One entry per octree level that contained points
 * @property {{ offsetX: number, offsetY: number, offsetZ: number }} transformData - SVO-local → world translation applied to decoded coordinates (equals the umbrella leaf's `svoBounds` min corner)
 */

/**
 * Options passed to {@link RcsFileLoader.loadFile}.
 * @typedef {Object} RcsLoadOptions
 * @property {boolean}  [skipPropertyDb] - When true, skip building the synthetic property database
 * @property {string}   [acmSessionId] - ACM session ID for proxy auth when loading ranger-hosted scans
 * @property {{ sortFuncType?: string }} [bvhOptions] - Options forwarded to BVH construction in {@link RcsPackage}
 * @property {THREE.Vector3|{ x: number, y: number, z: number }} [globalOffset] - Shared offset for multi-model aggregation. Defaults to the world-space bbox center (auto-centering) when omitted.
 * @property {Matrix4}  [placementTransform] - Alignment or placement override matrix, applied to the model during loading.
 * @property {boolean}  [applyRefPoint] - Whether to apply the file's native placement (the header TRS; "refPoint" is SVF terminology). Defaults to `false`, matching SVF. If false, `placementTransform` overrides the native placement entirely.
 * @property {string|{ from?: string, to?: string }} [applyScaling] - Unit-scaling applied on load, e.g. `{ from: 'ft', to: 'm' }`. If `from` is omitted it is read from metadata (RCS has none, so defaults to meters); a bare string is shorthand for `{ to: <string> }`.
 * @property {number[]} [forceWorldUpDirection] - If set, rotate the model so its up-axis aligns with this world up `[x,y,z]`. RCS models are Z-up ({@link RcsPackage} `RCS_WORLD_UP`).
 * @property {number[]} [overrideModelUpDirection] - Override the model's own up direction `[x,y,z]` (used with `forceWorldUpDirection`, and by `Model.getUpVector`).
 * @property {string} [overrideDistanceUnit] - Override the model's distance unit (used with `applyScaling`, and by `Model.getUnitString`).
 * @property {boolean} [applyPlacementInModelUnits] - Only relevant when both `placementTransform` and `applyScaling` are used: if true, placement is applied in model units (before scaling); if false (default), after scaling in viewer units.
 */

/**
 * RCS File Loader for LMV — loads RCS point cloud files and creates an
 * out-of-core rendering structure for progressive streaming.
 *
 * Model data (metadata, fragments, BVH, per-node arrays, urn) is held by
 * {@link RcsPackage}, which is the `myData` object for `Model`.
 * The loader is responsible for file I/O, material creation, fragment
 * activation/deactivation, and progress reporting.
 */
export class RcsFileLoader {

  /** @type {number} */
  static loadedFragments = 0;
  /** @type {Object.<number, number>} */
  static lodNodeCounts = {};
  /** @type {number} */
  static totalReceivedBytes = 0;
  /** @type {Array<number>} */
  static nodeLoadTimestamps = [];
  /** @type {number} */
  static totalNodesLoaded = 0;
  /** @type {number} */
  static lastStatsSecond = -1;
  /** @type {number} */
  static cachedNodesPerSecond = 0;
  /** @type {boolean} */
  static loadStatsTrackingEnabled = false;

  /**
   * Enable or disable per-node load statistics (for debugging overlays).
   * Off by default so {@link nodeLoadTimestamps} does not grow during normal use.
   * @param {boolean} enabled - When true, {@link getStats} tracks per-LOD counts and load rate
   */
  static setLoadStatsTrackingEnabled(enabled) {
    const wasEnabled = this.loadStatsTrackingEnabled;
    this.loadStatsTrackingEnabled = enabled;
    if (enabled && !wasEnabled) {
      this.resetLoadStats();
    } else if (!enabled) {
      this.nodeLoadTimestamps = [];
      this.cachedNodesPerSecond = 0;
      this.lastStatsSecond = -1;
    }
  }

  /** Clear per-node load counters while keeping global byte totals. */
  static resetLoadStats() {
    this.lodNodeCounts = {};
    this.totalNodesLoaded = 0;
    this.nodeLoadTimestamps = [];
    this.cachedNodesPerSecond = 0;
    this.lastStatsSecond = -1;
  }

  /**
   * Global loading statistics.
   * @returns {RcsLoadStats}
   */
  static getStats() {
    const now = performance.now();
    const currentSecond = Math.floor(now / 1000);

    if (currentSecond !== this.lastStatsSecond) {
      const oneSecondAgo = now - 1000;
      while (this.nodeLoadTimestamps.length > 0 && this.nodeLoadTimestamps[0] < oneSecondAgo) {
        this.nodeLoadTimestamps.shift();
      }
      this.cachedNodesPerSecond = this.nodeLoadTimestamps.length;
      this.lastStatsSecond = currentSecond;
    }

    const innerNodesLoaded = this.lodNodeCounts[-1] || 0;
    let totalFragmentsLoaded = innerNodesLoaded;
    for (const lodLevel in this.lodNodeCounts) {
      if (Number(lodLevel) !== -1) {
        totalFragmentsLoaded += this.lodNodeCounts[lodLevel];
      }
    }

    return {
      lodNodeCounts: this.lodNodeCounts,
      inFlightRequests: rcsWorkerPool.inFlightRequests,
      inFlightRequestsSize: rcsWorkerPool.inFlightRequestsSize,
      totalReceivedBytes: this.totalReceivedBytes + rcsWorkerPool.totalReceivedBytes,
      nodesLoadedPerSecond: this.cachedNodesPerSecond,
      totalNodesLoaded: this.totalNodesLoaded,
      innerNodesLoaded,
      totalFragmentsLoaded,
      cacheHits: RcsOpfsCacheStats.cacheHits,
      cacheMisses: RcsOpfsCacheStats.cacheMisses,
      cacheBytesLoaded: RcsOpfsCacheStats.cacheBytesLoaded,
      networkBytesLoaded: RcsOpfsCacheStats.networkBytesLoaded,
      lod0CacheHits: RcsOpfsCacheStats.lod0CacheHits,
      lod0CacheMisses: RcsOpfsCacheStats.lod0CacheMisses,
      opfsCacheEnabled: RcsOpfsCacheClient.opfsCacheEnabled
    };
  }

  /**
   * @param {Viewer3DImpl} parent - The viewer implementation that owns this loader instance
   */
  constructor(parent) {
    /** @type {Viewer3DImpl} */
    this.viewer3DImpl = parent;
    /**
     * Single-use guard: once {@link loadFile} has started this loader must not
     * be reused (same convention as SvfLoader / OtgLoader). Not cleared on
     * success or error — only {@link dtor} resets it.
     * @type {boolean}
     */
    this.loading = false;
    /** @type {string|null} */
    this.currentLoadPath = null;
    /**
     * Viewable URN parsed from `rcs:` load URLs. Used as the fallback in
     * {@link getAuthUrn} when no `acmSessionId` is available. {@link RcsPackage.urn}
     * stores the manifest root URN instead (OTG `svfUrn` convention).
     * @type {string|null}
     */
    this.urn = null;
    // Access token is read live via getToken() (see below), not a stored field, so
    // refreshes are always reflected.
    /** @type {Function|null} */
    this._onProgress = null;
    /** @type {Function|null} */
    this._onError = null;
    /** @type {Matrix4} */
    this._tmpMatrix = new Matrix4();
    /** @type {Box3} */
    this._tmpBox = new Box3();
    /** @type {number} */
    this.loadTime = 0;
    /** @type {boolean} */
    this.notifiesFirstPixel = true;
    /** @type {RcsExtractorCore|null} */
    this.rcsExtractor = null;
    /** @type {RcsPackage} - The model data package */
    // @ts-expect-error is null, assigned on load
    this.rcs = null;
    /** @type {THREE.PointCloudMaterial|null} */
    this.pointCloudMaterial = null;
    /** @type {{queryParams?: string, headers?: {Authorization?: string}}} */
    // @ts-expect-error is null, assigned on load
    this.loadContext = null;
    /** @type {RcsLoadOptions} */
    // @ts-expect-error is null, assigned on load
    this.options = null;
    /** @type {Model} */
    // @ts-expect-error is null, assigned on load
    this.model = null;
    /**
     * Per-loader OPFS cache session: opens and closes the worker-side cache session for
     * this model and reads/writes the bootstrap blobs (file header and umbrella octree).
     * @type {RcsOpfsCacheClient}
     */
    this.opfsCache = new RcsOpfsCacheClient();
    /** @type {boolean} Set by {@link dtor} so teardown runs at most once. */
    this._destroyed = false;

    // Progress monitoring
    /** @type {number} */
    this._coarseNodesLoaded = 0;
    /** @type {number} - Last progress percent we surfaced; used for ≥5% throttling. */
    this._lastReportedProgress = ROOT_LOADED_PROGRESS;

    RcsOpfsCacheClient.registerActiveLoader(this);
    rcsWorkerPool.register();
  }

  /**
   * Returns the current access token used to authenticate RCS resource requests.
   *
   * The viewer rotates this token before it expires (see `refreshToken` in envinit.js), so
   * callers must call this per request rather than caching the returned value.
   * @returns {string|null}
   */
  getToken() {
    return viewerToken.accessToken;
  }

  /**
   * Combined `urn,session` value as produced by {@link Document.load} for Fluent/SVF2/OTG
   * backends, matching OTG's `options.acmSessionId` — not the bare ACM session id alone.
   * @returns {string|null}
   */
  getAcmSessionId() {
    return this.options?.acmSessionId ?? null;
  }

  /**
   * URN sent in ranger WebSocket `/auth/{urn},...` messages.
   * Uses the manifest root URN from {@link getAcmSessionId} (like OTG's `svfUrn`),
   * not the nested viewable URN embedded in `rcs:` URLs — those diverge on shallow copies.
   * @returns {string|null}
   */
  getAuthUrn() {
    const acmSessionId = this.getAcmSessionId();
    if (acmSessionId) {
      return acmSessionId.split(',')[0];
    }
    return this.urn;
  }

  /**
   * Release extractor, property DB, worker-pool registration, and viewer references.
   * Resets the single-use {@link loading} guard so a new loader instance can be created.
   */
  dtor() {
    // Re-entry guard: the worker-pool slot is released exactly once below, and a second call
    // would unregister again and terminate the fetch worker under another still-active loader.
    if (this._destroyed) return;
    this._destroyed = true;

    RcsOpfsCacheClient.unregisterActiveLoader(this);
    this.opfsCache.drainInnerNodesCachedWaiters();

    const needsOpfsTeardown = this.opfsCache.initSent;
    if (needsOpfsTeardown) {
      this.opfsCache.scheduleTeardown();
    } else {
      rcsWorkerPool.unregister();
    }

    if (this.rcsExtractor) {
      this.rcsExtractor.close().catch((e) => console.error("Error closing RCS extractor:", e));
      this.rcsExtractor = null;
    }

    if (this.rcs && this.rcs.propDbLoader) {
      this.rcs.propDbLoader.dtor();
      this.rcs.propDbLoader = null;
    }

    // @ts-expect-error is null
    this.rcs = null;
    this.pointCloudMaterial = null;
    // @ts-expect-error is null
    this._tmpMatrix = null;
    // @ts-expect-error is null
    this._tmpBox = null;
    // @ts-expect-error is null
    this.viewer3DImpl = null;
    this.loading = false;
  }

  /**
   * Load an RCS file and create the Model.
   * @param {string} url - Direct file/HTTP URL, or `rcs:<urn>` for ranger-hosted scans
   * @param {RcsLoadOptions} options - Load-time options forwarded to {@link RcsPackage}
   * @param {function(Error|null, Model): void} [onLoad] - Called with `(null, model)` when the model root is ready
   * @param {function({type: string, percent: number, message: string}): void} [onProgress] - Coarse-loading progress callback
   * @param {function(Error): void} [onError] - Called if root load fails
   * @returns {boolean} `false` if already loading or the loader was destructed; `true` if load was started
   */
  loadFile(url, options, onLoad, onProgress, onError) {
    if (this.loading) {
      console.warn("RCS loader is already loading a file");
      return false;
    }

    if (!this.viewer3DImpl) {
      console.log("RCS loader was already destructed. So no longer usable.");
      return false;
    }

    this.loadContext = initLoadContext({});
    // initLoadContext calls getQueryParams, which may attach derivative-CDN params
    // (domain, bypassds, etc.). RCS does not use that pipeline — only auth headers
    // and occasional direct API URLs — so clear them. Passing queryParams: '' into
    // initLoadContext would not work (empty string is falsy and gets overwritten).
    this.loadContext.queryParams = '';
    rcsWorkerPool.syncProxyConfig();

    this.loading = true;
    this.loadTime = Date.now();
    this.options = options;
    this.currentLoadPath = url;

    this._loadModelRoot(url, options, onLoad, onProgress, onError);

    return true;
  }

  /**
   * Parse the RCS header, build {@link RcsPackage}, create the {@link Model}, and
   * dispatch root-load events. Fragment streaming is driven by the OOC tile manager
   * after this returns.
   * @private
   * @param {string} url - Same URL passed to {@link loadFile}
   * @param {RcsLoadOptions} options - Load options stored on the loader for later use
   * @param {function(Error|null, Model): void} [onLoad] - Root-load success callback
   * @param {function({type: string, percent: number, message: string}): void} [onProgress] - Stored for coarse-progress reporting
   * @param {function(Error): void} [onError] - Root-load failure callback
   * @returns {Promise<void>}
   */
  async _loadModelRoot(url, options, onLoad, onProgress, onError) {
    // Assign progress/error callbacks first so they are available to any
    // progress event triggered (now or later) during root load — e.g.
    // onModelRootLoadDone / _setupProgressMonitoring and the coarse-node
    // streaming path (onCoarseNodeLoaded -> _reportCoarseLoadingProgress).
    this._onProgress = onProgress ?? null;
    this._onError = onError ?? null;

    // For `rcs:` URLs the path embeds the viewable-node URN (see Document.getFullPath).
    // model.myData.urn uses the manifest root URN via getAuthUrn() — same as OTG svfUrn.
    /** @type {string|null} */
    let viewableUrn = null;

    // For the old ranger mode, we generate a signed URL for the data file from the Data Management API.
    // For the new ranger mode, we use the URN to identify the resource and no data URL is needed.
    let rcsFileUrl = /** @type {string|null} */null;
    if (url.startsWith('rcs:')) {
      viewableUrn = url.substring(4).replace('.rcs', '');
      if (USE_OLD_RANGER) {
        // Very hacky, we use the global export because it gets overwritte by viewer3d.html.
        // We need a cleaner way to pass those, maybe we options?
        // @ts-ignore
        const projectId = globalThis.Autodesk.Viewing.Private.getParameterByName('projectId');
        // @ts-ignore
        const docsEnv = globalThis.Autodesk.Viewing.Private.getParameterByName('docsEnv') ?? 'staging';
        let developerApiUrl = getDeveloperApiUrl();

        let dataManagementApiSuffix = docsEnv === 'QA' ? 'forge-dm-qa/data/v1/projects/' : '/data/v1/projects/';
        const dataManagementAPIUrl = developerApiUrl + `/${dataManagementApiSuffix}/b.${encodeURIComponent(projectId)}/versions/${encodeURIComponent(fromUrlSafeBase64(viewableUrn))}`;

        let dataManagementAPIResponse = await new Promise((resolve, fail) => {
          ViewingService.rawGet(dataManagementAPIUrl, '', '', resolve, fail, this.loadContext);
        });

        dataManagementAPIResponse = JSON.parse(dataManagementAPIResponse);
        let storageUrl = dataManagementAPIResponse.data?.relationships?.storage?.meta?.link?.href;
        if (!storageUrl) {
          throw new Error('Storage URL not found in Data Management API response');
        }
        let signedDataUrl = await ViewingService.getSignedS3DownloadUrl(storageUrl, 60, false);
        rcsFileUrl = signedDataUrl;
      }
    } else {
      // If the URL points directly to a RCS file, we set it here
      rcsFileUrl = url;
    }

    this.urn = viewableUrn;

    // Initialize RCS extractor. The access token is supplied as a getter that reads the
    // live token per request (never cached), so token refreshes are picked up; see
    // `getToken()` above. Proxy auth uses getAuthUrn() (manifest URN), not viewableUrn.
    this.rcsExtractor = new RcsExtractorCore(
      rcsFileUrl, () => this.getAuthUrn(), () => this.getToken(), () => this.getAcmSessionId()
    );

    try {
      if (!USE_OUT_OF_CORE_TILE_MANAGER) {
        throw new Error("OutOfCoreTileManager is not enabled");
      }

      const t0 = Date.now();

      await this.rcsExtractor.open();

      const bootstrapFromCache = await this.opfsCache.init({
        urn: viewableUrn, url, cacheDir: RCS_OPFS_CACHE_DIR
      });

      let loadedHeaderFromOpfs = false;
      let headerBytes;
      let chunk2Bytes;

      if (bootstrapFromCache && this.opfsCache.ready) {
        const cachedRaw = await this.opfsCache.loadHeaderAndOctree();
        if (cachedRaw) {
          headerBytes = cachedRaw.headerBytes;
          chunk2Bytes = cachedRaw.chunk2Bytes;
          loadedHeaderFromOpfs = true;
          console.info("RCS header+octree loaded from OPFS cache");
        }
      }

      // Cold load: read the header first, since parsing it yields the chunk directory
      // that locates chunk 2.
      if (!headerBytes) {
        headerBytes = await this.rcsExtractor.readHeaderBytes();
      }

      const { header, chunks } = this.rcsExtractor.parseHeaderBytes(headerBytes);

      if (!chunk2Bytes) {
        chunk2Bytes = await this.rcsExtractor.readOctreeChunkBytes();
      }

      const umbrellaLeafNodes = RcsParser.parseUmbrellaOctreeData(chunk2Bytes);
      this.rcsExtractor.umbrellaOctreeData = umbrellaLeafNodes;
      this.rcsExtractor.umbrellaExtendedData = RcsParser.computeUmbrellaExtendedData(
        header.svoBounds, umbrellaLeafNodes
      );

      const octreeStructure = RcsParser.buildOctreeStructure(umbrellaLeafNodes, this.rcsExtractor.umbrellaExtendedData);

      let directModeLevelsInLod0 = null;
      let loadedLevelsFromOpfs = false;
      if (this.opfsCache.ready) {
        directModeLevelsInLod0 = await this.opfsCache.loadDirectModeLevelsInLod0();
        loadedLevelsFromOpfs = directModeLevelsInLod0 !== null;
      }
      if (directModeLevelsInLod0 === null) {
        directModeLevelsInLod0 = computeDirectModeLevelsInLod0(umbrellaLeafNodes);
        if (directModeLevelsInLod0 > 0) {
          console.info(`RCS auto-selected LOD-0 source levels: ${directModeLevelsInLod0}`);
        }
      } else {
        console.info(`RCS LOD-0 source levels from OPFS cache: ${directModeLevelsInLod0}`);
      }

      if (this.opfsCache.ready) {
        // Read once at model open: the cacheable LOD depth is baked into the cache client
        // for the lifetime of the model, so changing the preference needs a model reload.
        const budget = this.viewer3DImpl?.api?.prefs?.get(POINT_CLOUD_CACHE_LIMIT) ??
        POINT_CLOUD_CACHE_PRESETS['8 GB'];
        this.opfsCache.maxCachedLodLevel =
        computeMaxCachedLodLevel(umbrellaLeafNodes, directModeLevelsInLod0, budget);
        if (Number.isFinite(this.opfsCache.maxCachedLodLevel)) {
          console.info(`RCS OPFS cache limited to LOD level ${this.opfsCache.maxCachedLodLevel}`);
        }
      }

      if (this.opfsCache.ready) {
        if (!loadedHeaderFromOpfs) {
          this.opfsCache.storeHeaderAndOctree(
            headerBytes, chunk2Bytes, directModeLevelsInLod0
          );
        } else if (!loadedLevelsFromOpfs) {
          this.opfsCache.storeDirectModeLevelsInLod0(directModeLevelsInLod0);
        }
      }

      // Build fragment ID map: (umbrellaIndex, lodLevel) -> fragId
      const numUmbrellaNodes = umbrellaLeafNodes.length;
      const fragmentIdMap = new Map();
      let nextFragId = 0;
      for (let ui = 0; ui < numUmbrellaNodes; ui++) {
        for (const lod of getRequiredLodLevels(umbrellaLeafNodes[ui].numSvoLeafNodes, directModeLevelsInLod0)) {
          fragmentIdMap.set(ui * MAX_LOD_LEVELS + lod, nextFragId++);
        }
      }

      // Build RcsPackage (BVH, per-node arrays, fragment data)
      this.rcs = new RcsPackage(
        header, chunks, umbrellaLeafNodes, octreeStructure, fragmentIdMap, options, directModeLevelsInLod0, this.getAuthUrn()
      );
      this.opfsCache.initInnerNodeCompletion(this.rcs);
      // Models with no inner nodes are vacuously "all inner nodes cached".
      if (this.opfsCache.innerNodeCompletion.size === 0) {
        this.opfsCache.signalAllInnerNodesCached();
      }

      // Create the Model using the package as its data
      this.model = new Model(this.rcs, this);
      // RCS streams nodes in/out of core; recycle freed geometry slots.
      this.model.getGeometryList().enableSlotReuse();
      // Empty / placeholder models (no umbrella leaves) have no BVH — see
      // RcsPackage._initEmptyModel. setBVH(null) would throw in ModelIteratorBVH.
      if (this.rcs.bvh) {
        this.model.setBVH(this.rcs.bvh, true);
      }
      this.onModelRootLoadDone();

      if (onLoad) {
        onLoad(null, this.model);
      }

      // @ts-ignore - dispatchEvent is inherited via a MixIn that is not supported in TS
      this.viewer3DImpl.api.dispatchEvent({
        type: et.MODEL_ROOT_LOADED_EVENT,
        rcs: this.rcs,
        model: this.model
      });

      // Start progress monitoring after MODEL_ROOT_LOADED so the final
      // GEOMETRY_LOADED (onLoadComplete) for empty models cannot precede it.
      this._setupProgressMonitoring();

      const t1 = Date.now();
      console.info("RCS root load: " + (t1 - t0));

    } catch (error) {
      console.error("Error loading RCS file:", error);
      if (onError) {
        onError(/** @type {Error} */error);
      }
    }
  }

  /**
   * Finalize the model root: register the point-cloud material, optionally build
   * the property DB, and signal `ROOT_LOADED` progress to the viewer.
   * @private
   */
  onModelRootLoadDone() {
    this.rcs.fragments.numLoaded = 0;

    this._createAndRegisterMaterial();

    if (!this.options.skipPropertyDb) {
      this.loadPropertyDb();
    }

    this.viewer3DImpl._signalNoMeshes();
    this.viewer3DImpl.signalProgress(ROOT_LOADED_PROGRESS, ProgressState.ROOT_LOADED, this.model, this.rcs.metadata.stats.num_fragments);
  }

  /**
   * Create and load the synthetic {@link RcsPropDbLoader} instance tree.
   * @private
   */
  loadPropertyDb() {
    // Viewer3D implements the EventDispatcher interface via a mixin not supported in TS
    const eventTarget = /** @type {import('../../application/EventDispatcher').EventDispatcher} */ /** @type {unknown} */this.viewer3DImpl.api;
    this.rcs.propDbLoader = INSTANCE_TREE_MODE === 'simple' ?
    new RcsSimplePropDbLoader(this.model, eventTarget) :
    new RcsDebugOctreePropDbLoader(this.model, eventTarget);
    this.rcs.propDbLoader.load();
  }

  /**
   * Create the shared {@link THREE.PointCloudMaterial} and register it with the
   * material manager under {@link RCS_POINTCLOUD_MATERIAL_ID}.
   * @private
   */
  _createAndRegisterMaterial() {
    this.pointCloudMaterial = new THREE.PointCloudMaterial({
      name: 'RCS_PointCloud',
      vertexColors: true,
      size: 2.0
    });

    const matHash = this.viewer3DImpl.matman()._getMaterialHash(this.model, RCS_POINTCLOUD_MATERIAL_ID);
    this.viewer3DImpl.matman().addMaterial(matHash, this.pointCloudMaterial);
  }

  /**
   * Create point geometry, add it to the model, set up the mesh, and
   * activate the fragment. This is the single entry point for turning
   * decoded (or pre-allocated) point data into a visible fragment.
   *
   * Underscore prefix: "protected" for this class — only the out-of-core loading task
   * (LoadRcsNodeTask) should call this, not host or external integration code.
   *
   * @param {number} fragId - Fragment to activate
   * @param {Object} options - Decoded (or placeholder) point data for the fragment
   * @param {number} options.pointCount - Number of points
   * @param {boolean} [options.innerFragment] - Stats-only flag: when true, skip per-second node load counters (inner HLOD nodes are still counted in {@link RcsFileLoader.lodNodeCounts}).
   * @param {Float32Array} [options.interleavedData] - Interleaved XYZRGB data. If omitted, a NaN-filled buffer is allocated (for HLOD inner nodes that are filled later).
   * @returns {LeanBufferGeometry} The created geometry
   */
  _addAndActivateFragment(fragId, options) {
    const geometry = this._createPointGeometry(fragId, options);

    const geomId = this.model.getGeometryList().addGeometry(geometry, 1);

    const fragmentList = this.model.getFragmentList();
    fragmentList.getOriginalWorldMatrix(fragId, this._tmpMatrix);

    const mesh = this.viewer3DImpl.setupMesh(
      this.model, geometry, RCS_POINTCLOUD_MATERIAL_ID, this._tmpMatrix
    );
    this.model.activateFragment(fragId, mesh);
    this.rcs.fragments.numLoaded++;

    this.viewer3DImpl.signalNewGeometryAdded(geomId, this.model);

    if (RcsFileLoader.loadStatsTrackingEnabled) {
      const nodeId = this.model.getIterator().getBVH().fragId2nodeId[fragId];
      const lodLevel = this.rcs.getNodeLodLevel(nodeId);

      RcsFileLoader.lodNodeCounts[lodLevel] = (RcsFileLoader.lodNodeCounts[lodLevel] || 0) + 1;

      if (!options.innerFragment) {
        RcsFileLoader.nodeLoadTimestamps.push(performance.now());
        const oneSecondAgo = performance.now() - 1000;
        while (RcsFileLoader.nodeLoadTimestamps.length > 0 &&
        RcsFileLoader.nodeLoadTimestamps[0] < oneSecondAgo) {
          RcsFileLoader.nodeLoadTimestamps.shift();
        }
        RcsFileLoader.totalNodesLoaded++;
      }
    }

    return geometry;
  }

  /**
   * Create LMV BufferGeometry with interleaved point data.
   *
   * @private
   * @param {number} fragId - Fragment ID
   * @param {Object} options - Point data used to populate the geometry buffers
   * @param {number} options.pointCount - Number of points
   * @param {Float32Array} [options.interleavedData] - Interleaved XYZRGB data
   * @returns {LeanBufferGeometry}
   */
  _createPointGeometry(fragId, options) {
    const { pointCount, interleavedData } = options;
    const stride = RCS_FLOATS_PER_POINT;

    let vb = interleavedData;
    if (!vb) {
      vb = new Float32Array(pointCount * RCS_FLOATS_PER_POINT);
      vb.fill(NaN);
    }

    // WebGL requires an index buffer.
    let indices = null;
    if (!USE_WEBGPU) {
      const useUint32 = pointCount > 65535;
      indices = useUint32 ? new Uint32Array(pointCount) : new Uint16Array(Math.ceil(pointCount / 2) * 2);
      for (let i = 0; i < pointCount; i++) {
        indices[i] = i;
      }
      if (indices.length !== pointCount) {
        indices[pointCount] = 0;
      }
    }

    /** @type {{ mesh: MeshData }} */
    const meshData = {
      mesh: {
        vb: vb,
        vbstride: stride,
        vblayout: {
          position: { offset: 0, bytesPerItem: 4, itemSize: 3 },
          color: { offset: 3, bytesPerItem: 4, itemSize: 3, normalized: false }
        },
        indices: indices,
        isPoints: true,
        pointSize: 2.5,
        boundingBox: this._getLocalFragmentBoundingBox(fragId)
      }
    };

    return BufferGeometryUtils.meshToGeometry(meshData);
  }

  /**
   * Compute the local-space bounding box for a fragment from its scan-space
   * extents. Vertices are 0-based scan-local, so the local box is
   * (0,0,0) → (extent-x, extent-y, extent-z). The extents are precomputed in
   * `RcsPackage._initFragmentTransformAndBox`.
   * @private
   * @param {number} fragId - Fragment whose local extents in {@link RcsPackage.fragments} are read
   * @returns {LmvBox3} Local AABB with min at the origin and max at the fragment extent
   */
  _getLocalFragmentBoundingBox(fragId) {
    const localExtents = this.rcs.fragments.localExtents;
    const o = fragId * 3;
    const box = new LmvBox3();
    box.min.set(0, 0, 0);
    box.max.set(localExtents[o], localExtents[o + 1], localExtents[o + 2]);
    return box;
  }

  /**
   * Deactivate a fragment (swap its geometry out of core).
   * Called by the OOC tile manager when a node is demoted from GPU memory.
   * @param {number} fragId - Fragment to swap out of core
   */
  _deactivateFragment(fragId) {
    const fl = this.model.getFragmentList();

    // Nothing to free if the fragment isn't currently resident (already swapped out of core
    // or marked not-loaded). Note we must not key this off visibility (MESH_HIDE / isFragOff):
    // a fragment can be loaded yet hidden (e.g. by the Filter extension or isolate), and such
    // a fragment must still be freed when it is swapped out.
    if (!fl.isInstanceLoaded(fragId)) {
      return;
    }

    // Set flag for Swap out of core directly. We don't call unload() because it forces MESH_HIDE on,
    // but MESH_HIDE is reserved for user-controlled visibility (Filter/isolate) and must
    // survive swap-out/swap-in cycles.
    fl.setFlagFragment(fragId, MeshFlags.MESH_SWAPPED_OUT_OF_CORE, true);

    if (RcsFileLoader.loadStatsTrackingEnabled) {
      const nodeId = this.model.getIterator().getBVH().fragId2nodeId[fragId];
      const lodLevel = this.rcs.getNodeLodLevel(nodeId);
      if (RcsFileLoader.lodNodeCounts[lodLevel]) {
        RcsFileLoader.lodNodeCounts[lodLevel]--;
      }
    }

    const geomCache = this.viewer3DImpl.geomCache(/* create */false);
    fl.geoms.removeInstance(fl.geomids[fragId], geomCache);
    // Clear stale geomid so a recycled slot can't alias unrelated geometry while swapped out.
    fl.geomids[fragId] = INVALID_GEOM_ID;
    this.rcs.fragments.numLoaded--;
    // @ts-ignore
    this.viewer3DImpl.api.dispatchEvent({ type: FRAGMENTS_UNLOADED_EVENT, model: this.model });
  }

  /**
   * OOC stage pipeline configuration for RCS point-cloud streaming.
   * @returns {OutOfCoreTileManagerStageConfiguration[]}
   */
  getStageConfiguration() {
    const isLocalDevLoad = this.currentLoadPath &&
    !this.currentLoadPath.startsWith('rcs:') &&
    isLocalhostUrl(this.currentLoadPath);
    if (isLocalDevLoad) {
      if (!localhostLoadWarningLogged) {
        localhostLoadWarningLogged = true;
        console.warn(
          `RCS: loading from localhost is for debugging only; ` +
          `reducing parallel geometry requests to ${RCS_NUM_IN_FLIGHT_REQUESTS_HTTP}.`
        );
      }
      return getRcsStageConfiguration().map((stage) =>
      stage.stage === StageNames.GEOMETRY_LOADED ?
      { ...stage, inAsyncPromotionLimit: RCS_NUM_IN_FLIGHT_REQUESTS_HTTP } :
      stage
      );
    }
    return getRcsStageConfiguration();
  }

  /**
   * Initialize loading-progress tracking.
   *
   * Progress is driven by the "coarse" node set (interior HLOD nodes + LOD-0
   * umbrella leaves) rather than total fragment count. Coarse fragments are
   * marked resident by RcsPackage.getLoadingScreenSpaceError, so once they
   * are all loaded the model has its complete coarsest-LOD representation
   * and we report 100%. Finer LOD-chain fragments (LOD 1+) keep streaming
   * on demand.
   *
   * @private
   */
  _setupProgressMonitoring() {
    this._coarseNodesLoaded = 0;
    this._lastReportedProgress = ROOT_LOADED_PROGRESS;

    this.viewer3DImpl.signalProgress(ROOT_LOADED_PROGRESS, ProgressState.LOADING, this.model);

    // Edge case: a model with no coarse nodes (empty / placeholder) is
    // already done as soon as the root has been loaded.
    if (this.rcs.numCoarseNodes === 0) {
      this._reportCoarseLoadingProgress(100);
    }
  }

  /**
   * Notify the loader that a coarse BVH node has finished loading.
   * Called by {@link LoadRcsNodeTask} after each coarse-node task executes.
   * No parameters — progress is derived from {@link RcsPackage.numCoarseNodes}.
   */
  onCoarseNodeLoaded() {
    // Guard against the callback being called asynchronously after teardown.
    if (!this.rcs || !this.viewer3DImpl) return;

    if (this.rcs.loadDone) {
      // TODO: This works for the moment, but at some point we will need the ability to unload
      //       coarse nodes to enable loading multiple point clouds. At that point, this might happen
      console.warn("onCoarseNodeLoaded called after load was already done");
      return;
    }

    this._coarseNodesLoaded++;

    const total = this.rcs.numCoarseNodes;
    const loaded = this._coarseNodesLoaded;
    const progressPercent = Math.min(ROOT_LOADED_PROGRESS + Math.floor(loaded / total * (100 - ROOT_LOADED_PROGRESS)), 100);

    this._reportCoarseLoadingProgress(progressPercent);
  }

  /**
   * Surface the current coarse-loading progress to the onProgress callback
   * and the viewer's progress event.
   * @param {number} progressPercent - Current overall load progress in [0, 100].
   * @private
   */
  _reportCoarseLoadingProgress(progressPercent) {
    if (this.rcs.loadDone) return;

    //Throttle events to >=5% increments, but always fire the final 100% notification.
    if (progressPercent < this._lastReportedProgress + 5 && progressPercent < 100) {
      return;
    }
    this._lastReportedProgress = progressPercent;

    if (this._onProgress) {
      this._onProgress({
        type: 'progress',
        percent: progressPercent,
        message: `Loading point clouds... ${this._coarseNodesLoaded}/${this.rcs.numCoarseNodes} coarse nodes ready`
      });
    }

    if (progressPercent < 100) {
      this.viewer3DImpl.signalProgress(progressPercent, ProgressState.LOADING, this.model);
    } else {
      this.rcs.loadDone = true;
      // onLoadComplete signals 100% via signalProgress and dispatches
      // GEOMETRY_LOADED_EVENT, mirroring the OTG / SVF loaders.
      this.viewer3DImpl.onLoadComplete(this.model);
    }
  }
}

// Initialize RcsIO reference for byte tracking
setRcsFileLoaderReference(RcsFileLoader);

/**
 * Register the RCS file loader with the FileLoaderManager. Gated behind the
 * `EnablePointCloud` internal feature flag and invoked from the flag's
 * initialization callback in {@link FeatureFlags}, so RCS support is opt-in.
 * @private
 */
export function registerRcsFileLoader() {
  FileLoaderManager.registerFileLoader("rcs", ["rcs"], RcsFileLoader);
}